import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';
import * as g from '../../castle-kit-grounds';

/**
 * ALCHEMY LAB (Алхимическая лаборатория) — the castle's arcane workshop (isolated).
 *
 * Composition: ONE undisputed HERO — the bubbling ALEMBIC on the central work-bench, its green-violet
 * glow the single brightest beat, ringed by a faint floor SIGIL. A lone alchemist's STOOL sits at the
 * bench so a duck visibly hunches over the work. Supporting accents hug the walls (potion scroll-shelves
 * + a spell-book lectern) framing an open centre, with a simmering CAULDRON as the one secondary arcane
 * prop. A single candelabra throws warm light onto the bench (no competing glow-pools) and two herb
 * banners give the only sanctioned green. Decluttered: removed the duplicate votive light, the rival
 * crystal-orb "second hero", a redundant torch, the off-narrative lily and the third banner — the eye
 * now lands straight on the alembic. Mysterious, lamplit, never grim.
 *
 * Geometry: zone (3180,760) 380×420 → centre (3370,970). Doors N@x3400, W@y970, S@y1180 — mouths kept clear.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── HERO group: arcane sigil under the bench → cluttered bench → the bubbling alembic → the work-stool. ──
  place(ctx, g.spellCircle(art, 70), 3370, 960); // floor sigil (laid first, under everything)
  place(ctx, kit.smallTable(art, 40), 3370, 960); // cluttered work-bench
  place(ctx, g.alembic(art), 3370, 920); // HERO — bubbling apparatus, the brightest beat
  place(ctx, kit.woodStool(art), 3370, 1000); // SEAT (alchemy-stool) — duck hunches at the bench

  // ── Supporting accents hug the walls, framing the open centre. ──
  place(ctx, kit.scrollShelf(art, 70, 90), 3220, 860); // NW potion shelf
  place(ctx, kit.scrollShelf(art, 70, 70), 3520, 860); // NE potion shelf
  place(ctx, kit.scrollShelf(art, 70, 84), 3520, 1080); // SE potion shelf
  place(ctx, kit.lectern(art), 3250, 1080); // spell-book lectern (SW, off the W/S door mouths)

  // ── The one secondary arcane prop: a simmering cauldron + its steam (SE quarter). ──
  place(ctx, kit.cauldron(art), 3470, 1110);
  place(ctx, kit.steamHint(), 3470, 1086);

  // ── A single warm pool: candelabra throws light onto the bench, plus one N-wall torch. ──
  place(ctx, kit.candelabra(art), 3300, 1000);
  place(ctx, kit.wallTorch(art), 3340, 800);

  // ── Hanging dried HERBS — two greenish banners (the room's only sanctioned green). ──
  place(ctx, kit.banner(art, 0x5a7a3a, 70), 3200, 940); // W wall (above the W door)
  place(ctx, kit.banner(art, 0x6a8a3a, 70), 3540, 980); // E wall

  // ── One reagent cask tucked into the SW corner. ──
  place(ctx, kit.barrel(art), 3220, 1120);
};

export default decorate;
